import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Compass, Home as HomeIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

/**
 * 404 page rendered by the catch-all `path="*"` route.
 *
 * Signed-in members are offered a link back to their dashboard; signed-out visitors are
 * offered the landing page. Either way we always show a "Go back" button that uses the
 * browser history so users can recover from a mistyped URL or a stale deep-link.
 */
export function NotFoundPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const home = isAuthenticated ? "/app" : "/";
  const homeLabel = isAuthenticated ? "Back to dashboard" : "Back to home";

  return (
    <div
      className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-6 py-16 text-center"
      data-testid="not-found-page"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-gold/30 bg-elevated/40 text-gold">
        <Compass className="h-8 w-8" />
      </div>
      <h1 className="font-serif text-4xl text-text sm:text-5xl">404</h1>
      <p className="mt-3 text-lg text-text sm:text-xl">This page doesn&apos;t exist.</p>
      <p className="mt-2 max-w-md text-sm text-muted">
        We couldn&apos;t find anything at{" "}
        <code className="rounded bg-elevated px-1.5 py-0.5 text-xs text-text">{pathname}</code>.
        The link may be stale, or something in the URL was mistyped.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          data-testid="not-found-back"
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Go back
        </Button>
        <Button asChild data-testid="not-found-home" className="gap-2">
          <Link to={home}>
            <HomeIcon className="h-4 w-4" />
            {homeLabel}
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default NotFoundPage;
